import { Fragment, ReactNode } from 'react';
import { Box, SxProps, Theme } from '@mui/material';

interface FormattedTextProps {
	text: string;
	sx?: SxProps<Theme>;
}

const inlinePattern =
	/(\*\*[^*\n]+\*\*|__[^_\n]+__|\[[^\]\n]+\]\(https?:\/\/[^\s)]+\)|https?:\/\/[^\s<]+|`[^`\n]+`|\*[^*\n]+\*|_[^_\n]+_)/g;

function trimUrlPunctuation(value: string) {
	const match = value.match(/^(.*?)([.,!?;:]*)$/);
	return {
		url: match?.[1] || value,
		trailing: match?.[2] || '',
	};
}

function renderInline(value: string, keyPrefix: string): ReactNode[] {
	const output: ReactNode[] = [];
	let cursor = 0;
	let match: RegExpExecArray | null;
	let part = 0;
	const pattern = new RegExp(inlinePattern.source, 'g');

	while ((match = pattern.exec(value)) !== null) {
		if (match.index > cursor) {
			output.push(value.slice(cursor, match.index));
		}

		const token = match[0];
		const key = `${keyPrefix}-${part++}`;
		if (
			(token.startsWith('**') && token.endsWith('**')) ||
			(token.startsWith('__') && token.endsWith('__'))
		) {
			output.push(
				<Box component="strong" key={key} sx={{ fontWeight: 700 }}>
					{renderInline(token.slice(2, -2), `${key}-bold`)}
				</Box>,
			);
		} else if (token.startsWith('[')) {
			const link = token.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/);
			output.push(
				link ? (
					<Box
						component="a"
						key={key}
						href={link[2]}
						target="_blank"
						rel="noopener noreferrer"
						sx={{ color: '#007B67', textDecoration: 'underline' }}>
						{link[1]}
					</Box>
				) : (
					token
				),
			);
		} else if (token.startsWith('http')) {
			const { url, trailing } = trimUrlPunctuation(token);
			output.push(
				<Fragment key={key}>
					<Box
						component="a"
						href={url}
						target="_blank"
						rel="noopener noreferrer"
						sx={{ color: '#007B67', textDecoration: 'underline' }}>
						{url}
					</Box>
					{trailing}
				</Fragment>,
			);
		} else if (token.startsWith('`')) {
			output.push(
				<Box
					component="code"
					key={key}
					sx={{
						px: 0.45,
						py: 0.1,
						borderRadius: '3px',
						backgroundColor: 'rgba(17, 27, 33, 0.08)',
						fontFamily: 'monospace',
						fontSize: '0.92em',
					}}>
					{token.slice(1, -1)}
				</Box>,
			);
		} else {
			output.push(
				<Box component="em" key={key} sx={{ fontStyle: 'italic' }}>
					{renderInline(token.slice(1, -1), `${key}-italic`)}
				</Box>,
			);
		}

		cursor = match.index + token.length;
	}

	if (cursor < value.length) output.push(value.slice(cursor));
	return output;
}

function FormattedText({ text, sx }: FormattedTextProps) {
	const lines = text.replace(/\r\n?/g, '\n').split('\n');

	return (
		<Box
			component="div"
			sx={{
				wordBreak: 'break-word',
				overflowWrap: 'anywhere',
				...sx,
			}}>
			{lines.map((line, index) => {
				const heading = line.match(/^(#{1,6})\s+(.+)$/);
				const bullet = line.match(/^\s*[-*+]\s+(.+)$/);
				const numbered = line.match(/^\s*(\d+)[.)]\s+(.+)$/);
				const content =
					heading?.[2] || bullet?.[1] || numbered?.[2] || line;

				return (
					<Box
						component="span"
						key={index}
						sx={{
							display: 'block',
							minHeight: line ? undefined : '0.75em',
							pl: bullet || numbered ? 1.75 : 0,
							position: 'relative',
							fontWeight: heading ? 700 : 'inherit',
							fontSize: heading
								? `${Math.max(1, 1.28 - heading[1].length * 0.05)}em`
								: 'inherit',
							mt: heading && index > 0 ? 0.65 : 0,
						}}>
						{bullet && (
							<Box
								component="span"
								sx={{ position: 'absolute', left: 0 }}>
								•
							</Box>
						)}
						{numbered && (
							<Box
								component="span"
								sx={{ position: 'absolute', left: 0 }}>
								{numbered[1]}.
							</Box>
						)}
						{renderInline(content, `line-${index}`)}
					</Box>
				);
			})}
		</Box>
	);
}

export default FormattedText;
